import type { CreateAccountItem, GroupAccountItem } from './types'
import type { MutationError } from '../../../vendors/types'
import type { PremiumGroup } from '../../types'
import type { FormValues } from '../types'
import type { FormInstance } from 'antd'
import type { ZodIssue } from 'zod'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Col, Divider, Form, Input, Row, Select, Typography } from 'antd'
import { isAxiosError } from 'axios'
import React, { useState } from 'react'

import $api from '../../../../API'
import { InvoiceProcessingLevel } from '../../../../domain/invoice-processing-level.enum'
import './index.css'
import { PushType } from '../../../../domain/push-type.enum'
import { MaskedInput } from '../../../vendors/components/masked-input'
import { ValidationError } from '../../../vendors/errors/validation-error'

import { GroupAccountWrapper } from './group-account-wrapper'

export const GroupAccount = ({
  group,
  form,
  error
}: {
  group: PremiumGroup
  form: FormInstance<FormValues>
  error?: Error
  billingTypes: string[]
}): React.JSX.Element => {
  const queryClient = useQueryClient()
  const validationError = error as Partial<ValidationError> | undefined
  const fundingType = Form.useWatch('premium_funding_type', form)
  const level = Form.useWatch('invoice_processing_level', form)

  const [creating, setCreating] = useState(false)
  const [createForm] = Form.useForm<CreateAccountItem>()

  const isPull = fundingType === PushType.Pull

  const isDivisions = isPull && level === InvoiceProcessingLevel.Division

  async function invalidateAccountsQuery(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['divisions', group.id] })
  }

  const mutation = useMutation<GroupAccountItem[], MutationError, Omit<GroupAccountItem, 'id'>>({
    async mutationFn(data: Omit<GroupAccountItem, 'id'>) {
      const url = `/groups/accounts/${group.id}`

      try {
        const response = await $api.post<GroupAccountItem[]>(url, {
          ...data,
          routing_number: data.routing_number.trim().replaceAll(/\D/g, '')
        })
        return response.data
      } catch (error) {
        if (isAxiosError<{ issues: ZodIssue[] }>(error) && error.response?.status === 422) {
          throw new ValidationError(error.response.data.issues)
        }

        throw new Error('Failed to update group')
      }
    },
    onMutate(variables) {
      queryClient.setQueryData(['divisions', group.id], (oldData: unknown) => {
        // Properly type the oldData
        const typedOldData = oldData as
          | { divisions?: string[]; accounts?: GroupAccountItem[] }
          | undefined

        return {
          divisions: typedOldData?.divisions ?? [],
          accounts: [
            ...(typedOldData?.accounts ?? []),
            {
              ...variables,
              id: new Date().toISOString()
            }
          ]
        }
      })
    }
  })

  const addItemMutation = useMutation<
    string[],
    MutationError,
    { accountId: string; data: string[] }
  >({
    async mutationFn({ accountId, data }: { accountId: string; data: string[] }) {
      const url = `/groups/accounts/${accountId}/items`

      try {
        const response = await $api.post<string[]>(url, {
          items: data
        })
        return response.data
      } catch {
        throw new Error('Failed to update group')
      }
    },
    onMutate(variabled) {
      queryClient.setQueryData(['divisions', group.id], (oldData: unknown) => {
        const typedOldData = oldData as
          | { divisions?: string[]; accounts?: GroupAccountItem[] }
          | undefined

        return {
          divisions: typedOldData?.divisions ?? [],
          accounts:
            typedOldData?.accounts?.map(account =>
              account.id === variabled.accountId
                ? { ...account, items: [...account.items, ...variabled.data] }
                : account
            ) ?? []
        }
      })
    }
  })

  const drpopItemMutation = useMutation<
    string[],
    MutationError,
    { accountId: string; data: string[] }
  >({
    async mutationFn({ accountId, data }: { accountId: string; data: string[] }) {
      const url = `/groups/accounts/${accountId}/items`

      try {
        const response = await $api.delete<string[]>(url, {
          data: {
            items: data
          }
        })
        return response.data
      } catch {
        throw new Error('Failed to update group')
      }
    },
    onMutate(variables) {
      queryClient.setQueryData(['divisions', group.id], (oldData: unknown) => {
        const typedOldData = oldData as
          | { divisions?: string[]; accounts?: GroupAccountItem[] }
          | undefined

        return {
          divisions: typedOldData?.divisions ?? [],
          accounts:
            typedOldData?.accounts?.map(account =>
              account.id === variables.accountId
                ? {
                    ...account,
                    items: account.items.filter(item => !variables.data.includes(item))
                  }
                : account
            ) ?? []
        }
      })
    }
  })

  const divisionsQuery = useQuery({
    queryKey: ['divisions', group.id],
    queryFn: async ({ queryKey }) => {
      const [, id] = queryKey
      const response = await $api.get<{ divisions: string[]; accounts: GroupAccountItem[] | null }>(
        `/groups/${id}/accounts`
      )
      return response.data
    },
    enabled: isDivisions,
    keepPreviousData: true
  })

  const accounts = divisionsQuery.data?.accounts

  const handleSave = (): void => {
    const values = createForm.getFieldsValue()
    const newAccount: Omit<GroupAccountItem, 'id'> = {
      ...values,
      type: level === InvoiceProcessingLevel.Division ? 'division' : 'line',
      items: []
    }

    mutation.mutate(newAccount, {
      onSuccess: () => {
        setCreating(false)
        invalidateAccountsQuery().catch((e: unknown) => {
          console.info(e)
        })
        createForm.resetFields()
      }
    })
  }

  const mutationError = mutation.error as Partial<ValidationError> | undefined

  const addItemToItems = (id: string, val: string): void => {
    if (Array.isArray(accounts)) {
      addItemMutation.mutate(
        {
          accountId: id,
          data: [val]
        },
        {
          onSuccess: () => {
            invalidateAccountsQuery().catch((e: unknown) => {
              console.info(e)
            })
          }
        }
      )
    }
  }

  const dropItemFromItems = (id: string, val: string): void => {
    if (Array.isArray(accounts) && !drpopItemMutation.isLoading) {
      drpopItemMutation.mutate(
        {
          accountId: id,
          data: [val]
        },
        {
          onSuccess: () => {
            invalidateAccountsQuery().catch((e: unknown) => {
              console.info(e)
            })
          }
        }
      )
    }
  }

  const selectedDivisions = accounts?.flatMap(x => x.items) ?? []

  if (isDivisions) {
    return (
      <GroupAccountWrapper
        withCreate
        creating={creating}
        onClick={() => {
          setCreating(true)
        }}
        onCancel={() => {
          setCreating(false)
        }}
        onSave={handleSave}
        loading={mutation.isLoading}
      >
        {creating && (
          <Col span={24}>
            <Form form={createForm} className='w-full' layout='vertical'>
              <Row gutter={32}>
                <Col span={24}>
                  <Form.Item
                    name='label'
                    label={
                      <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                        New Account Label
                      </span>
                    }
                    validateStatus={mutationError?.issues?.label ? 'error' : undefined}
                    help={mutationError?.issues?.label}
                    required
                  >
                    <Input size='middle' className='w-full rounded-md' />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={32}>
                <Col span={12}>
                  <Form.Item
                    name='routing_number'
                    label={
                      <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                        Client Routing Number
                      </span>
                    }
                    validateStatus={mutationError?.issues?.routing_number ? 'error' : undefined}
                    help={mutationError?.issues?.routing_number}
                    required
                  >
                    <MaskedInput
                      mask='999999999'
                      size='middle'
                      className='w-full rounded-md'
                      disabled={false}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='account_number'
                    label={
                      <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                        Client Account NumBer
                      </span>
                    }
                    validateStatus={mutationError?.issues?.account_number ? 'error' : undefined}
                    help={mutationError?.issues?.account_number}
                    required
                  >
                    <Input size='middle' className='w-full rounded-md' />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        )}
        {!creating && Array.isArray(accounts) && (
          <Col span={24}>
            {accounts.length > 0 && <Divider className='mb-4 mt-4' />}
            {accounts.map(val => (
              <div key={val.id}>
                <Row gutter={12} className='items-center'>
                  <Col span={8}>
                    <Typography.Text strong>{val.label}</Typography.Text>
                  </Col>
                  <Col span={16}>
                    <Typography.Text strong>Linked divisions</Typography.Text>
                  </Col>
                </Row>
                <Row gutter={12} className='mt-2'>
                  <Col span={8}>
                    <Typography.Text className='text-gray-400'>
                      ***{val.routing_number.slice(-4)} {val.account_number}
                    </Typography.Text>
                  </Col>
                  <Col span={16}>
                    <Select
                      mode='multiple'
                      autoClearSearchValue
                      disabled={addItemMutation.isLoading || drpopItemMutation.isLoading}
                      loading={addItemMutation.isLoading || drpopItemMutation.isLoading}
                      size='middle'
                      value={val.items}
                      className='w-full rounded-md'
                      placeholder='Add Division'
                      onDeselect={selectedItem => {
                        dropItemFromItems(val.id, selectedItem)
                      }}
                      onSelect={selectedItem => {
                        addItemToItems(val.id, selectedItem)
                      }}
                      options={(divisionsQuery.data?.divisions ?? [])
                        .map(d => ({ value: d, label: d }))
                        .filter(item => !selectedDivisions.includes(item.value))}
                    />
                  </Col>
                </Row>
                <Divider className='mb-4 mt-4' />
              </div>
            ))}
          </Col>
        )}
      </GroupAccountWrapper>
    )
  }

  return (
    <GroupAccountWrapper>
      <Col>
        <Form.Item
          name='premium_routing_number'
          label={
            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
              Group Routing Number
            </span>
          }
          validateStatus={validationError?.issues?.premium_routing_number ? 'error' : undefined}
          help={validationError?.issues?.premium_routing_number}
          required
        >
          <MaskedInput
            mask='999999999'
            size='middle'
            className='w-full rounded-md'
            placeholder={isPull ? 'E.g. 104000854' : '–'}
            disabled={!isPull}
          />
        </Form.Item>
      </Col>
      <Col>
        <Form.Item
          name='premium_account_number'
          label={
            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
              Group Account Number
            </span>
          }
          validateStatus={validationError?.issues?.premium_account_number ? 'error' : undefined}
          help={validationError?.issues?.premium_account_number}
          required
        >
          <Input
            size='middle'
            className='w-full rounded-md'
            placeholder={isPull ? 'E.g. 702144500' : '–'}
            disabled={!isPull}
          />
        </Form.Item>
      </Col>
    </GroupAccountWrapper>
  )
}
